<script lang="ts">
	import Watchers from 'assets/svg/watchers.svg?url';
	import WatchersWhite from 'assets/svg/watchersWhite.svg?url';
	import Star from 'assets/svg/star.svg?url';
	import StarWhite from 'assets/svg/starWhite.svg?url';
	import Forks from 'assets/svg/forks.svg?url';
	import ForksWhite from 'assets/svg/forksWhite.svg?url';
	import { onMount } from 'svelte';
	import GithubTable from './GithubTable/GithubTable.svelte';
	import calculateTimeScale from './calculateTimeScale';
	import GithubSectionHeader from './GithubSectionHeader/GithubSectionHeader.svelte';
	import ColorScale from './ColorScale/ColorScale.svelte';
	import isInViewport from 'utils/isInViewport';
	import { throttle } from 'utils/decorators';
	import GithubLoader from 'components/GithubLoader/GithubLoader.svelte';
	import { _ } from 'utils/i18/i18n';
	import { getGithubOrganizationData } from 'services/github/organization-data';
	import { browser } from '$app/environment';
	import IntersectionObserver from 'components/IntersectionObserver/IntersectionObserver.svelte';

	export let dark = false;
	export let githubUrl;

	let gitOrganizationData = {};
	let error = false;
	const timeScale = calculateTimeScale(new Date());

	let isInUserViewport = false;
	let isLoaded = false;
	let githubBlockRef;

	async function getGithubData(type = 'core') {
		if (!browser) return;
		const data = await getGithubOrganizationData(githubUrl, type);
		gitOrganizationData = data.gitOrganizationData;
		error = data.error;
	}

	onMount(() => {
		/** The github data loading task must be delayed by a few milliseconds to correctly determine value "isInUserViewport"*/
		setTimeout(() => {
			if (githubBlockRef) isInUserViewport = isInViewport(githubBlockRef);
			if (gitOrganizationData && isInUserViewport) {
				isLoaded = true;
				getGithubData();
			} else {
				document.addEventListener('scroll', handleScroll);
			}
		}, 500);

		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	});

	const handleScroll = throttle(() => {
		if (githubBlockRef) isInUserViewport = isInViewport(githubBlockRef, 1000);
	}, 100);

	$: if (isInUserViewport && !isLoaded) {
		isLoaded = true;
		getGithubData();
		document.removeEventListener('scroll', handleScroll);
	}
</script>

{#if githubUrl && !error}
	<section>
		<GithubSectionHeader {dark} url={githubUrl} getData={getGithubData} />
		{#key gitOrganizationData}
			<div class="github-container" class:dark bind:this={githubBlockRef}>
				<IntersectionObserver let:intersecting once={true} top={200}>
					{#if intersecting}
						{#if !gitOrganizationData.hasOwnProperty('data')}
							<GithubLoader {dark} />
						{:else}
							<h2>{$_('coinDetailed.gitHubSection.pastYearOfDeveloperActivity')}</h2>
							<div class="github-table-header">
								<div class="github-table-header-item" class:dark>
									<img src={dark ? WatchersWhite : Watchers} width="14" height="12" alt="" />
									<p>{$_('coinDetailed.gitHubSection.watchers')}</p>
									<div class="github-table-header-item-count">
										{gitOrganizationData?.watchers.toLocaleString('en-US') ?? 0}
									</div>
								</div>
								<div class="github-table-header-item" class:dark>
									<img src={dark ? StarWhite : Star} width="14" height="12" alt="" />
									<p>{$_('coinDetailed.gitHubSection.stargazers')}</p>
									<div class="github-table-header-item-count">
										{gitOrganizationData?.stargazers.toLocaleString('en-US') ?? 0}
									</div>
								</div>
								<div class="github-table-header-item" class:dark>
									<img src={dark ? ForksWhite : Forks} width="14" height="12" alt="" />
									<p>{$_('coinDetailed.gitHubSection.forks')}</p>
									<div class="github-table-header-item-count">
										{gitOrganizationData?.forks.toLocaleString('en-US') ?? 0}
									</div>
								</div>
								<ColorScale class="github-color-scale" />
							</div>
							{#if gitOrganizationData?.data && timeScale}
								<GithubTable commits={gitOrganizationData?.data} {timeScale} {dark} />
							{/if}
							<ColorScale class="github-color-scale github-color-scale-bottom" />
							<div class="github-table-footer">
								<div class="github-table-footer-item" class:dark>
									{$_('coinDetailed.gitHubSection.repositories')}
									<p>{gitOrganizationData?.repos.toLocaleString('en-US') ?? 0}</p>
								</div>
								<div class="github-table-footer-item" class:dark>
									{$_('coinDetailed.gitHubSection.contributors')}
									<p>{gitOrganizationData?.contributors.toLocaleString('en-US') ?? 0}</p>
								</div>
								<div class="github-table-footer-item" class:dark>
									{$_('coinDetailed.gitHubSection.commitsThisYear')}
									<p>{gitOrganizationData?.commits?.toLocaleString('en-US') ?? 0}</p>
								</div>
							</div>
						{/if}
					{/if}
				</IntersectionObserver>
			</div>
		{/key}
	</section>
{/if}

<style lang="scss">
	@import 'githubSection';
</style>
